import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Alert, Modal, Form, Card, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import EventManager from '../components/EventManager';
import EventForm from '../components/EventForm';
import EventList from '../components/EventList';
import { NSSC_YEAR } from '../data/config';

const EventDashboard = () => {
    const [eventManager] = useState(() => new EventManager());
    const [events, setEvents] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [directPushPassword, setDirectPushPassword] = useState('');
    const [githubToken, setGithubToken] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [lastSyncTime, setLastSyncTime] = useState(null);
    // Push-in-flight state. Null when idle; { kind: 'pr' | 'direct', message }
    // while a save is running. Drives the button spinner and the busy overlay.
    const [savingState, setSavingState] = useState(null);
    const isSaving = savingState !== null;

    useEffect(() => {
        initializeEventManager();
        checkAuthentication();
    }, []);

    const initializeEventManager = async () => {
        try {
            setLoading(true);
            const loadedEvents = await eventManager.loadEvents();
            setEvents(loadedEvents);
            setHasUnsavedChanges(eventManager.hasUnsavedChanges());
        } catch (error) {
            toast.error(`Failed to load events: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const checkAuthentication = () => {
        const isAuth = eventManager.githubAPI.isAuthenticated();
        const hasEnvToken = !!process.env.REACT_APP_GITHUB_TOKEN;
        setIsAuthenticated(isAuth);
        if (isAuth) {
            setLastSyncTime(new Date());
        }
        // Show different messages based on auth status
        if (hasEnvToken) {
            toast.success('Data fetched successfully');
        } else if (isAuth) {
            toast.info('Using fallback GitHub token.');
        }
    };

    const handleAddEvent = async (eventData) => {
        try {
            // Events are indexed by slug — the slug from the form IS the id.
            const newId = eventData.slug;
            if (!newId) {
                throw new Error('Slug is required.');
            }
            if (events[newId]) {
                throw new Error(`An event with slug "${newId}" already exists.`);
            }

            eventManager.updateEvent(newId, eventData);
            setEvents({ ...eventManager.getEvents() });
            setHasUnsavedChanges(eventManager.hasUnsavedChanges());
            setShowForm(false);
            toast.success(`Event "${eventData.name}" added successfully!`);
        } catch (error) {
            toast.error(`Failed to add event: ${error.message}`);
            throw error;
        }
    };

    const handleEditEvent = (eventId, eventData) => {
        setSelectedEvent({ id: eventId, data: eventData });
        setShowForm(true);
    };

    const handleUpdateEvent = async (eventData) => {
        try {
            if (!selectedEvent) return;

            // If the slug was renamed, treat it as move: delete old key + add new.
            if (eventData.slug && eventData.slug !== selectedEvent.id) {
                if (eventManager.getEvent(eventData.slug)) {
                    throw new Error(`An event with slug "${eventData.slug}" already exists.`);
                }
                eventManager.deleteEvent(selectedEvent.id);
                eventManager.updateEvent(eventData.slug, eventData);
            } else {
                eventManager.updateEvent(selectedEvent.id, eventData);
            }

            setEvents({ ...eventManager.getEvents() });
            setHasUnsavedChanges(eventManager.hasUnsavedChanges());
            setSelectedEvent(null);
            setShowForm(false);
            toast.success(`Event "${eventData.name}" updated successfully!`);
        } catch (error) {
            toast.error(`Failed to update event: ${error.message}`);
            throw error;
        }
    };

    const handleDeleteEvent = async (eventId) => {
        try {
            const success = eventManager.deleteEvent(eventId);
            if (success) {
                setEvents({ ...eventManager.getEvents() });
                setHasUnsavedChanges(eventManager.hasUnsavedChanges());
                toast.success('Event deleted successfully!');
            }
        } catch (error) {
            toast.error(`Failed to delete event: ${error.message}`);
        }
    };

    const handleGitHubAuth = () => {
        if (githubToken.trim()) {
            eventManager.githubAPI.setToken(githubToken.trim());
            setIsAuthenticated(true);
            setShowAuthModal(false);
            setGithubToken('');
            toast.success('Data fetched successfully');
        } else {
            toast.error('Please enter a valid GitHub token');
        }
    };

    const handleSyncWithRemote = async () => {
        try {
            const success = await eventManager.syncWithRemote();
            if (success) {
                setEvents({ ...eventManager.getEvents() });
                setHasUnsavedChanges(eventManager.hasUnsavedChanges());
                setLastSyncTime(new Date());
                toast.success('Successfully synced with GitHub!');
            }
        } catch (error) {
            toast.error(`Sync failed: ${error.message}`);
        }
    };

    // Build a per-target status list from a save result. `kind` picks the
    // wording ('PR' vs 'commit') and which URL field to link.
    const summarizeResult = (result, kind) => {
        const urlField = kind === 'pr' ? 'pullRequestUrl' : 'commitUrl';
        const label = kind === 'pr' ? 'PR' : 'commit';
        const rows = [];
        for (const name of ['frontend', 'backend', 'admin']) {
            const entry = result?.[name];
            if (!entry) {
                rows.push({ name, ok: false, text: 'skipped' });
            } else if (entry.success) {
                rows.push({ name, ok: true, text: `${label} ready`, url: entry[urlField] });
            } else {
                rows.push({ name, ok: false, text: entry.message || `${label} failed` });
            }
        }
        return rows;
    };

    // Render a rich toast body from a summary. We use JSX so success rows link
    // out directly instead of dumping raw URLs into the toast string.
    const renderResultToast = (title, rows) => (
        <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{title}</div>
            {rows.map((r) => (
                <div key={r.name} style={{ fontSize: '0.85em', lineHeight: 1.4 }}>
                    <span style={{ textTransform: 'capitalize' }}>{r.name}</span>:{' '}
                    {r.ok && r.url ? (
                        <a
                            href={r.url}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: '#8ecae6', textDecoration: 'underline' }}
                        >
                            {r.text}
                        </a>
                    ) : (
                        <span style={{ color: r.ok ? '#8ecae6' : '#ff6b6b' }}>{r.text}</span>
                    )}
                </div>
            ))}
        </div>
    );

    const handleCreatePR = async () => {
        if (!isAuthenticated) {
            toast.error('Please authenticate with GitHub first');
            return;
        }
        if (isSaving) return;

        setSavingState({ kind: 'pr', message: 'Opening pull requests…' });
        try {
            const result = await eventManager.saveChanges('Update events via admin dashboard');
            if (result?.noChanges) {
                toast.info('No changes to save.');
                return;
            }

            const rows = summarizeResult(result, 'pr');
            if (result?.success) {
                setHasUnsavedChanges(false);
                toast.success(renderResultToast('Pull requests opened', rows), { autoClose: 8000 });
            } else if (rows.some((r) => r.ok)) {
                toast.error(
                    renderResultToast('Partial — retry to open the missing PR(s)', rows),
                    { autoClose: false }
                );
            } else {
                toast.error(
                    renderResultToast(result?.message || 'Failed to open pull requests', rows),
                    { autoClose: false }
                );
            }
        } catch (error) {
            toast.error(`Failed to save changes: ${error.message}`, { autoClose: false });
        } finally {
            setSavingState(null);
        }
    };

    const handleSaveDirectly = async () => {
        if (!isAuthenticated) {
            toast.error('Please authenticate with GitHub first');
            return;
        }
        if (isSaving) return;

        // Show password modal
        setShowPasswordModal(true);
    };

    const handleConfirmDirectPush = async () => {
        const CORRECT_PASSWORD = process.env.REACT_APP_DIRECT_PUSH_PASSWORD;

        if (directPushPassword !== CORRECT_PASSWORD) {
            toast.error('Incorrect password! Only events head can push directly.');
            setDirectPushPassword('');
            return;
        }

        // Close modal and reset password
        setShowPasswordModal(false);
        setDirectPushPassword('');

        setSavingState({ kind: 'direct', message: 'Pushing to configured branches…' });
        try {
            const result = await eventManager.githubAPI.pushToBranches(
                eventManager.getEvents(),
                'Update events via admin dashboard (direct push)'
            );

            const rows = summarizeResult(result, 'commit');
            if (result?.success) {
                setHasUnsavedChanges(false);
                toast.success(renderResultToast('Changes pushed', rows), { autoClose: 8000 });
            } else if (rows.some((r) => r.ok)) {
                toast.error(
                    renderResultToast('Partial push — retry to fix the missing repo(s)', rows),
                    { autoClose: false }
                );
            } else {
                toast.error(
                    renderResultToast(result?.message || 'Failed to push changes', rows),
                    { autoClose: false }
                );
            }
        } catch (error) {
            toast.error(`Failed to push changes: ${error.message}`, { autoClose: false });
        } finally {
            setSavingState(null);
        }
    };

    const handleCancelForm = () => {
        setSelectedEvent(null);
        setShowForm(false);
    };

    if (loading) {
        return (
            <Container className="mt-4">
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading events...</p>
                </div>
            </Container>
        );
    }

    return (
        <Container fluid className="mt-4 event-dashboard">
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <p className="text-muted">
                                Manage events for NSSC {NSSC_YEAR}
                                {lastSyncTime && (
                                    <span className="ms-2">
                                        Last sync: {lastSyncTime.toLocaleTimeString()}
                                    </span>
                                )}
                            </p>
                        </div>
                        <div className="d-flex gap-2">
                            {!isAuthenticated && (
                                <Button 
                                    variant="outline-warning" 
                                    onClick={() => setShowAuthModal(true)}
                                >
                                     Connect GitHub
                                </Button>
                            )}
                            {hasUnsavedChanges && isAuthenticated && (
                                <>
                                    <Button
                                        variant="primary"
                                        onClick={handleCreatePR}
                                        disabled={isSaving}
                                    >
                                        {savingState?.kind === 'pr' ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                />
                                                Opening PRs…
                                            </>
                                        ) : (
                                            'Ask to save'
                                        )}
                                    </Button>
                                    <Button
                                        variant="success"
                                        onClick={handleSaveDirectly}
                                        disabled={isSaving}
                                    >
                                        {savingState?.kind === 'direct' ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                />
                                                Pushing…
                                            </>
                                        ) : (
                                            'Save Directly'
                                        )}
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>

            {!isAuthenticated && (
                <Row className="mb-3">
                    <Col>
                        <Alert variant="warning">
                            <strong>GitHub Integration:</strong> Add your GitHub token to <code>.env</code> file or click "Connect GitHub" to enable repository sync and pull request creation.
                            <br />
                            <small>Add <code>REACT_APP_GITHUB_TOKEN=your_token_here</code> to your <code>.env</code> file for automatic authentication.</small>
                        </Alert>
                    </Col>
                </Row>
            )}

            {isSaving && (
                <Row className="mb-3">
                    <Col>
                        <Alert variant="info" className="d-flex align-items-center mb-0">
                            <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                            />
                            <div>
                                <strong>{savingState.message}</strong>
                                <div style={{ fontSize: '0.85em', opacity: 0.85 }}>
                                    Talking to GitHub — this can take up to ~10 seconds per repo. Don't close this tab.
                                </div>
                            </div>
                        </Alert>
                    </Col>
                </Row>
            )}

            <Row>
                <Col>
                    {showForm ? (
                        <EventForm
                            eventData={selectedEvent?.data}
                            onSubmit={selectedEvent ? handleUpdateEvent : handleAddEvent}
                            onCancel={handleCancelForm}
                            isEditing={!!selectedEvent}
                        />
                    ) : (
                        <EventList
                            events={events}
                            onEditEvent={handleEditEvent}
                            onDeleteEvent={handleDeleteEvent}
                            onSyncWithRemote={handleSyncWithRemote}
                            hasUnsavedChanges={hasUnsavedChanges}
                            isAuthenticated={isAuthenticated}
                        />
                    )}
                </Col>
            </Row>

            {/* GitHub Authentication Modal */}
            <Modal show={showAuthModal} onHide={() => setShowAuthModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Connect to GitHub</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="info">
                        <strong>Recommended:</strong> Add your GitHub token to the <code>.env</code> file:
                        <br />
                        <code>REACT_APP_GITHUB_TOKEN=your_token_here</code>
                        <br />
                        <small>This is more secure than entering it manually.</small>
                    </Alert>
                    <p>
                        <strong>Alternative:</strong> Manually enter your GitHub Personal Access Token below 
                        (will be stored in browser localStorage as fallback).
                    </p>
                    <Form>
                        <Form.Group>
                            <Form.Label>GitHub Personal Access Token</Form.Label>
                            <Form.Control
                                type="password"
                                value={githubToken}
                                onChange={(e) => setGithubToken(e.target.value)}
                                placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                            />
                            <Form.Text className="text-muted">
                                Create a token at GitHub Settings → Developer Settings → Personal Access Tokens.
                                Required permissions: <code>repo</code> (full control).
                            </Form.Text>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAuthModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleGitHubAuth}>
                        Connect (Fallback)
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Direct Push Password Modal */}
            <Modal 
                show={showPasswordModal} 
                onHide={() => {
                    setShowPasswordModal(false);
                    setDirectPushPassword('');
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>⚠️ Direct Push Authentication</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning">
                        <strong>Warning:</strong> Direct push commits immediately to the frontend, backend, and admin repositories on their configured deployment branches — no review, no PR.
                        Only the events head should have access to this feature.
                    </Alert>
                    <Form onSubmit={(e) => {
                        e.preventDefault();
                        handleConfirmDirectPush();
                    }}>
                        <Form.Group>
                            <Form.Label>Enter Password</Form.Label>
                            <Form.Control
                                type="password"
                                value={directPushPassword}
                                onChange={(e) => setDirectPushPassword(e.target.value)}
                                placeholder="Enter password to confirm"
                                autoFocus
                            />
                            <Form.Text className="text-muted">
                                This action requires authorization. Contact events head if you need access.
                            </Form.Text>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => {
                            setShowPasswordModal(false);
                            setDirectPushPassword('');
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleConfirmDirectPush}
                        disabled={!directPushPassword || isSaving}
                    >
                        {isSaving ? (
                            <>
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                />
                                Pushing…
                            </>
                        ) : (
                            'Confirm Direct Push'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default EventDashboard;